import type { DesignNode, ShapedComponent } from '@repo/api-contracts';
import { createRaster, type Raster } from '@repo/warp-engine';
import { compose, IDENTITY, type Matrix, multiply, scale } from '../geometry/matrix';
import { nodeMatrix } from '../geometry/node-transform';
import type { RenderMaterials } from '../materials';
import { applyShapeMask, rasterizeShapeMask } from '../raster/shape-mask';
import { drawRasterTransformed } from '../raster/transform-raster';
import { layoutText } from '../text/layout-text';
import { rasterizeText } from '../text/rasterize-text';
import type { CreateContext, MeasureText } from '../types';

export interface RenderComponentInput {
  readonly component: ShapedComponent;
  readonly layers: readonly DesignNode[];
  readonly materials: RenderMaterials;
  readonly pxPerMm: number;
  readonly createContext: CreateContext;
  readonly measure: MeasureText;
}

const drawLayers = (
  dst: Raster,
  layers: readonly DesignNode[],
  parent: Matrix,
  input: RenderComponentInput,
): void => {
  const { materials, pxPerMm } = input;
  const toPx = scale(pxPerMm, pxPerMm);
  for (const node of layers) {
    const matrix = multiply(parent, nodeMatrix(node));
    if (node.type === 'group') {
      drawLayers(dst, node.children, matrix, input);
      continue;
    }
    if (node.type === 'image') {
      const src = materials.images.get(node.assetId);
      if (src === undefined) continue;
      const fit = scale(node.width / src.width, node.height / src.height);
      drawRasterTransformed(dst, src, compose(toPx, matrix, fit));
      continue;
    }
    const font = materials.fonts.get(node.fontId);
    if (font === undefined) continue;
    const color = materials.inks.get(node.inkColorId) ?? '#000000';
    const layout = layoutText(node, font.familyName, input.measure);
    const raster = rasterizeText({
      node,
      layout,
      familyName: font.familyName,
      color,
      pxPerMm,
      createContext: input.createContext,
      measure: input.measure,
    });
    drawRasterTransformed(dst, raster, compose(toPx, matrix, scale(1 / pxPerMm, 1 / pxPerMm)));
  }
};

/** 컴포넌트 하나를 mm×pxPerMm 크기의 straight RGBA 래스터로 그린다. layers 순서대로, 그룹은 재귀(D13). */
export const renderComponent = (input: RenderComponentInput): Raster => {
  const width = Math.max(1, Math.ceil(input.component.widthMm * input.pxPerMm));
  const height = Math.max(1, Math.ceil(input.component.heightMm * input.pxPerMm));
  const dst = createRaster(width, height);
  drawLayers(dst, input.layers, IDENTITY, input);
  const mask = rasterizeShapeMask(input.component, width, height, input.pxPerMm);
  if (mask !== null) applyShapeMask(dst, mask);
  return dst;
};
